import React, { useState, ReactNode, useReducer } from 'react';
import { Mark, EditPositionType } from '..';
import useModalState from '../../../../../context/modal/useModalState';
import { UserPositionRaw } from '../../../../../hooks/web3/trade/useUserPositions';
import { formatEther, parseEther } from 'ethers/lib/utils';
import { formatPercentages, leverageMultiplier } from '../../../../../helpers';
import {
    OrderBookType,
    OrderDataTypes,
    Validators,
} from '../../../../../config/constants';
import { config } from '../../../../../config/config';
import { ExtendPositionToLeverageParamsStruct } from '../../../../../config/contracts/typechain/ITradeManager';
import { BigNumber } from 'ethers';
import LeverageSlider from '../../../../../components/LeverageSlider';
import LimitPriceDropdown from '../../LimitPriceDropdown';
import { preventOverflow } from '../../../../../web3/utils';
import AdjustPreview from './AdjustPreview';
import calculateBankruptcyPrice from '../../../../../helpers/calculations/calculateBankruptcyPrice';
import { UserOrder } from '../../../../../hooks/web3/trade/useUserOpenOrders';
import useCancelOrder from '../../../../../hooks/web3/trade/useCancelOrder';
import LeverageInput from '../../../../../components/LeverageSlider/LeverageInput';
import useCreateOrder from '../../../../../hooks/web3/trade/useCreateOrder';

export type EditPositionAdjustProps = {
    marks: Mark[];
    orderCancelled: boolean;
    setOrderCancelled: React.Dispatch<React.SetStateAction<boolean>>;
    children(confirm: {
        confirm: () => Promise<void>;
        validators: Validators[];
    }): ReactNode;
};

const EditPositionAdjust: React.FC<EditPositionAdjustProps> = ({
    marks,
    orderCancelled,
    setOrderCancelled,
    children,
}) => {
    const { modalData } = useModalState();
    const createOrder = useCreateOrder();
    const cancel = useCancelOrder();

    const userPosition: UserPositionRaw | UserOrder | undefined = modalData.data.position;
    const userOrder =
        userPosition &&
        'orderData' in userPosition &&
        modalData.data.defaultEditTab === EditPositionType.ADJUST
            ? userPosition
            : undefined;

    const [leverage, setLeverage] = useState(
        userOrder?.orderData
            ? BigNumber.from(
                  (
                      userOrder.orderData as OrderDataTypes<ExtendPositionToLeverageParamsStruct>
                  ).params.targetLeverage
              )
                  .div(config.leverageMultiplier)
                  .toString()
            : parseFloat(userPosition?.leverage || '3').toFixed(2)
    );
    const [limitPrice, setLimitPrice] = useState(userOrder?.limitPrice || '');
    const [expiryDate, setExpiryDate] = useState<Date | undefined>(
        userOrder
            ? new Date(Number(userOrder.orderData.constraints.deadline) * 1000)
            : undefined
    );
    const [isLimitOrder, toggleIsLimitOrder] = useReducer(
        (prev) => !prev,
        !!userOrder?.limitPrice
    );
    const [withExpiryDate, toggleWithExpiryDate] = useReducer(
        (prev) => !prev,
        !!userOrder?.orderData.constraints.deadline
    );

    const validators: Validators[] = [
        {
            errorCondition: parseEther(leverage || '0').lte(
                parseEther(parseFloat(userPosition?.leverage || '1.1').toFixed(2))
            ),
            message: 'New Leverage must be higher than Current Leverage!',
        },
        {
            errorCondition:
                isLimitOrder && (!limitPrice || parseEther(limitPrice).isZero()),
            message: 'Limit Price is required!',
        },
        {
            errorCondition: withExpiryDate && !expiryDate,
            message: 'Expiry Date is required!',
        },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setLeverage(preventOverflow(value, 2));
    };

    const getBankruptcyPrice = (latestPrice: string) => {
        const addedSize = parseEther(userPosition?.margin || '1')
            .mul(parseEther(leverage || '1'))
            .div(parseEther('1'))
            .sub(parseEther(userPosition?.volume || '0'));

        const newSize = parseEther(userPosition?.volume || '0').add(addedSize);

        const newEntryPrice = newSize
            .mul(parseEther('1'))
            .div(
                parseEther(userPosition?.size || '0').add(
                    addedSize.mul(parseEther('1')).div(parseEther(latestPrice))
                )
            );

        return calculateBankruptcyPrice(
            formatEther(newEntryPrice),
            userPosition?.isShort,
            leverage
        );
    };

    const confirm = async () => {
        if (!userPosition) throw new Error('UserPosition is undefined');

        if (userOrder && !orderCancelled) {
            await cancel(userOrder.orderId, userOrder.orderType, () =>
                setOrderCancelled(true)
            );
            return;
        }

        await createOrder(
            OrderBookType.EXTEND_LEVERAGE,
            {
                positionId: userPosition.id,
                targetLeverage: leverageMultiplier(leverage).toString(),
                tradePair: userPosition.tradePair.address,
            },
            'ExtendPositionToLeverageOrder',
            userPosition.isShort,
            getBankruptcyPrice,

            {
                expiryDate: withExpiryDate ? expiryDate : undefined,
                limitPrice: isLimitOrder ? limitPrice : undefined,
                isMarketOrder: !limitPrice ? true : undefined,
                tradePair: userPosition.tradePair,
                userPosition,
            }
        );
    };

    return (
        <>
            <span className='position-type-text'>
                Increase your leverage without adding collateral
            </span>
            <div className='leverage-preview'>
                <div className='label-container'>
                    <span className='label'>New Leverage</span>
                    <span className='side-label existing'>Current Leverage</span>
                </div>
                <div className='preview'>
                    <LeverageInput
                        leverage={leverage}
                        maxLeverage={Number(userPosition?.tradePair?.maxLeverage || 100)}
                        onChange={handleChange}
                    />
                    <span className='existing'>
                        {formatPercentages(userPosition?.leverage || 0, 2)}x
                    </span>
                </div>
            </div>
            <LeverageSlider
                value={leverage}
                minLeverage={parseFloat(
                    userPosition?.leverage || config.minLeverage.toString()
                ).toFixed(2)}
                onChange={setLeverage}
                marks={marks}
                disabled={!!userOrder && !orderCancelled}
            />
            <LimitPriceDropdown
                selectedTradePair={userPosition?.tradePair}
                limitPriceChecked={isLimitOrder}
                toggleLimitPrice={toggleIsLimitOrder}
                orderExpiryChecked={withExpiryDate}
                toggleOrderExpiry={toggleWithExpiryDate}
                amount={limitPrice}
                onChange={(value) => setLimitPrice(value)}
                date={expiryDate}
                handleDateChange={setExpiryDate}
                disabled={!!userOrder && !orderCancelled}
            />
            <AdjustPreview
                leverage={leverage}
                userPosition={userPosition}
                isInitialLeverage={
                    parseFloat(userPosition?.leverage || '1.1').toFixed(2) === leverage
                }
            />
            {children({ confirm, validators })}
        </>
    );
};

export default EditPositionAdjust;
